import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CalendarPlus, Check, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import DatePickerPopover from "./DatePickerPopover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  plannerAccess,
  plannerNextSlot,
  createPlannerEvent,
  getPlannerSchedule,
} from "../lib/api";
import ScheduleUnitDialog from "./ScheduleUnitDialog";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

// Find the day-label that an ISO date maps to, given the teacher's
// schedule. For 'weekly' the mapping is calendar-day-of-week; for
// 'rotation' we rely on cycle_anchors. We don't reimplement rotation
// math here — instead we just fetch /planner/next-slot to learn the
// authoritative slot whenever the date changes.
const calendarDayLabelFor = (schedule, isoDateStr) => {
  if (!schedule || !isoDateStr) return null;
  if (schedule.schedule_type !== "weekly") return null;
  // Date string is YYYY-MM-DD, treat as local midnight to dodge TZ drift.
  const [y, m, d] = isoDateStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  const dow = dt.getDay(); // 0=Sun..6=Sat
  if (dow === 0 || dow === 6) return null;
  return WEEKDAYS[dow - 1];
};

// Pops up after a successful lesson/unit generation. Only renders for
// Pro users with an onboarded planner schedule. Skip-for-now is a
// first-class option — the lesson stays in My Library.
const PostGenScheduleDialog = ({ activity, onDismiss }) => {
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [isUnit, setIsUnit] = useState(false);
  const [nextSlot, setNextSlot] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [date, setDate] = useState("");
  const [periodLabel, setPeriodLabel] = useState("");
  const [scheduling, setScheduling] = useState(false);
  const [unitOpen, setUnitOpen] = useState(false);

  // Probe: is this Pro + onboarded? If not, never show. We also pull
  // the full schedule so the period <Select> has real period labels
  // to offer (instead of the old free-text input).
  useEffect(() => {
    if (!activity?.id) return;
    plannerAccess()
      .then((res) => {
        if (!(res.has_access && res.onboarded)) return;
        setEnabled(true);
        setOpen(true);
        // Summative tasks span N class periods → unit-scheduling flow.
        const multi =
          activity.output_type === "unit" ||
          activity.output_type === "summative_task";
        setIsUnit(multi);
        // Always grab the schedule so the Period select has options.
        getPlannerSchedule()
          .then((s) => setSchedule(s))
          .catch(() => {});
        if (!multi) {
          const subj = activity.inputs?.subject;
          if (subj) {
            plannerNextSlot(subj)
              .then((slot) => {
                if (slot?.date) {
                  setNextSlot(slot);
                  setDate(slot.date);
                  setPeriodLabel(slot.period_label || "");
                }
              })
              .catch(() => {});
          }
        }
      })
      .catch(() => {});
  }, [activity]);

  // The day-label that matches the picked date — drives which day
  // column from subject_map we inspect to flag "this period is normally
  // {subject}".
  const dayLabelForDate = useMemo(() => {
    if (!schedule || !date) return null;
    if (schedule.schedule_type === "weekly") {
      return calendarDayLabelFor(schedule, date);
    }
    // Rotation mode: we rely on nextSlot's day_label when present,
    // otherwise leave null and present every period without a subject hint.
    if (nextSlot?.date === date && nextSlot?.day_label) {
      return nextSlot.day_label;
    }
    return null;
  }, [schedule, date, nextSlot]);

  // Period options shown to the teacher. Breaks (Recess/Lunch) are
  // filtered out — you can't schedule a lesson into Lunch. Each option
  // carries an optional `subject` annotation for the day-label so the
  // teacher knows which period normally hosts the lesson's subject.
  const periodOptions = useMemo(() => {
    if (!schedule?.periods?.length) return [];
    const dayMap = (schedule.subject_map || {})[dayLabelForDate] || {};
    return schedule.periods
      .filter((p) => p.kind !== "break")
      .map((p) => ({
        label: p.label,
        time: `${p.start}–${p.end}`,
        scheduledSubject: dayMap[p.label] || "",
      }));
  }, [schedule, dayLabelForDate]);

  // If the teacher changes the date, re-pull next-slot to surface the
  // best matching period (and re-flag the day-label for rotation cycles).
  useEffect(() => {
    if (!date || !activity?.inputs?.subject || isUnit) return;
    plannerNextSlot(activity.inputs.subject, date)
      .then((slot) => {
        if (slot?.date === date) {
          setNextSlot(slot);
          // Only auto-fill period if user hasn't already touched it.
          if (!periodLabel) setPeriodLabel(slot.period_label || "");
        } else {
          // No match for that date — keep whatever the teacher picked.
          setNextSlot(null);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const handleSchedule = async () => {
    setScheduling(true);
    try {
      const ev = await createPlannerEvent({
        date,
        period_label: periodLabel,
        subject: activity.inputs?.subject || null,
        activity_id: activity.id,
        activity_name: activity.activity_name,
        walt: activity.tagline || "",
        kind: "lesson",
      });
      toast.success(`Added to Planner on ${ev.date} · ${ev.period_label}.`);
      setOpen(false);
      onDismiss && onDismiss();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not schedule.");
    } finally {
      setScheduling(false);
    }
  };

  const handleSkip = () => {
    setOpen(false);
    onDismiss && onDismiss();
  };

  if (!enabled) return null;

  if (isUnit) {
    return (
      <ScheduleUnitDialog
        open={open || unitOpen}
        onOpenChange={(o) => {
          setOpen(o);
          setUnitOpen(o);
          if (!o) onDismiss && onDismiss();
        }}
        activity={activity}
        onScheduled={() => {
          onDismiss && onDismiss();
        }}
      />
    );
  }

  const activitySubject = activity.inputs?.subject || "";

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) onDismiss && onDismiss(); }}>
      <DialogContent className="max-w-[calc(100vw-1.5rem)] sm:max-w-md max-h-[calc(100vh-2rem)] overflow-y-auto" data-testid="postgen-schedule-dialog">
        <DialogHeader>
          <DialogTitle>When would you like to schedule this?</DialogTitle>
          <DialogDescription>
            {activity.activity_name}
            {activitySubject ? ` · ${activitySubject}` : ""}
          </DialogDescription>
        </DialogHeader>

        {nextSlot ? (
          <p className="text-sm text-[#4a6984] bg-[#96ded1]/15 p-3 rounded">
            Next available {activitySubject || "subject"} slot:
            <strong className="text-[#1c2d3d] block mt-0.5">
              {nextSlot.day_label}, {nextSlot.date} · {nextSlot.period_label}
            </strong>
          </p>
        ) : (
          <p className="text-xs text-[#4a6984]/70">
            No automatic match — pick any date and period below.
          </p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-bold text-[#4a6984] uppercase tracking-[0.12em]">
              Date
            </Label>
            <DatePickerPopover
              value={date}
              onChange={setDate}
              testid="postgen-date"
              className="w-full"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold text-[#4a6984] uppercase tracking-[0.12em]">
              Period
              {dayLabelForDate ? (
                <span className="ml-1 normal-case font-medium text-[#4a6984]/70">
                  · {dayLabelForDate}
                </span>
              ) : null}
            </Label>
            <Select value={periodLabel} onValueChange={setPeriodLabel}>
              <SelectTrigger data-testid="postgen-period" className="w-full">
                <SelectValue placeholder="Pick a period" />
              </SelectTrigger>
              <SelectContent>
                {periodOptions.length === 0 ? (
                  <SelectItem value="__none__" disabled>
                    Schedule not loaded
                  </SelectItem>
                ) : (
                  periodOptions.map((p) => {
                    const matches =
                      activitySubject &&
                      p.scheduledSubject &&
                      p.scheduledSubject.toLowerCase() === activitySubject.toLowerCase();
                    return (
                      <SelectItem
                        key={p.label}
                        value={p.label}
                        data-testid={`postgen-period-${p.label.replace(/\s+/g, "-")}`}
                      >
                        <span className="inline-flex items-center gap-2">
                          {matches ? (
                            <Check className="h-3 w-3 text-[#1c9e7e]" />
                          ) : (
                            <span className="inline-block w-3" />
                          )}
                          <span className="font-bold text-[#1c2d3d]">{p.label}</span>
                          <span className="text-[10px] text-[#4a6984]/65">{p.time}</span>
                          {p.scheduledSubject ? (
                            <span
                              className={`ml-1 text-[10px] font-semibold ${
                                matches ? "text-[#1c9e7e]" : "text-[#4a6984]/60"
                              }`}
                            >
                              · {p.scheduledSubject}
                            </span>
                          ) : null}
                        </span>
                      </SelectItem>
                    );
                  })
                )}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={handleSkip} data-testid="postgen-skip">
            Skip for now
          </Button>
          <Button
            className="bg-[#1c2d3d] hover:bg-[#1c2d3d]/90 text-white"
            onClick={handleSchedule}
            disabled={!date || !periodLabel || periodLabel === "__none__" || scheduling}
            data-testid="postgen-confirm"
          >
            {scheduling ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <CalendarPlus className="h-3.5 w-3.5 mr-1" />}
            Add to Planner
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PostGenScheduleDialog;
